import { useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import type { ReviewGroup } from '../lib/tests';

/**
 * Moving a labelling gap around its picture, on the review screen.
 *
 * A paper places its gaps by writing "16 at 47% 44%", which is a percentage
 * found by eye and therefore approximately right. Rather than send a teacher
 * back to the template to nudge a number they cannot see the effect of, the
 * box is dragged where it belongs and the percentage follows.
 *
 * Every drop is saved as it happens. A paper is edited once and sat many
 * times; a Save button here would be one more thing to forget.
 */
export function PlacedLabels({
  testId,
  group,
  organizationId,
}: {
  testId: string;
  group: ReviewGroup;
  organizationId?: string;
}) {
  const art = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<Record<number, { x: number; y: number }>>(() =>
    Object.fromEntries(
      group.questions.filter((q) => q.at).map((q) => [q.number, q.at as { x: number; y: number }]),
    ),
  );
  const [dragging, setDragging] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<number | null>(null);

  if (!group.imageUrl || Object.keys(at).length === 0) return null;

  /** Where the pointer is, as a percentage of the picture. */
  const pointAt = (e: React.PointerEvent) => {
    const box = art.current?.getBoundingClientRect();
    if (!box) return null;
    return {
      x: Math.min(100, Math.max(0, ((e.clientX - box.left) / box.width) * 100)),
      y: Math.min(100, Math.max(0, ((e.clientY - box.top) / box.height) * 100)),
    };
  };

  async function save(number: number, next: { x: number; y: number }) {
    setError(null);
    try {
      await api.patch(`/tests/${testId}/questions/${number}`, {
        at: { x: Number(next.x.toFixed(1)), y: Number(next.y.toFixed(1)) },
        ...(organizationId ? { organizationId } : {}),
      });
      setSaved(number);
      window.setTimeout(() => setSaved((n) => (n === number ? null : n)), 1200);
    } catch (err) {
      setError(errorMessage(err, 'That position could not be saved'));
    }
  }

  return (
    <div className="stack">
      <p className="field__hint">
        Drag a box to where the answer belongs on the picture. Arrow keys nudge it; hold shift to
        move further. Every move is saved.
      </p>
      {error && <p className="field__error">{error}</p>}

      <div className="placed" ref={art}>
        <img src={group.imageUrl} alt="" />
        {Object.entries(at).map(([number, point]) => (
          <button
            key={number}
            type="button"
            className="placed__slot"
            aria-label={`Question ${number}, ${Math.round(point.x)} per cent across, ${Math.round(
              point.y,
            )} per cent down`}
            data-moving={dragging === Number(number) || undefined}
            data-saved={saved === Number(number) || undefined}
            style={{ left: `${point.x}%`, top: `${point.y}%` }}
            onPointerDown={(e) => {
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              setDragging(Number(number));
            }}
            onPointerMove={(e) => {
              if (dragging !== Number(number)) return;
              const next = pointAt(e);
              if (next) setAt((prev) => ({ ...prev, [number]: next }));
            }}
            onPointerUp={() => {
              if (dragging !== Number(number)) return;
              setDragging(null);
              void save(Number(number), at[Number(number)]!);
            }}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 5 : 1;
              const moves: Record<string, [number, number]> = {
                ArrowLeft: [-step, 0],
                ArrowRight: [step, 0],
                ArrowUp: [0, -step],
                ArrowDown: [0, step],
              };
              const move = moves[e.key];
              if (!move) return;
              e.preventDefault();
              const current = at[Number(number)]!;
              const next = {
                x: Math.min(100, Math.max(0, current.x + move[0])),
                y: Math.min(100, Math.max(0, current.y + move[1])),
              };
              setAt((prev) => ({ ...prev, [number]: next }));
              void save(Number(number), next);
            }}
          >
            {number}
          </button>
        ))}
      </div>
    </div>
  );
}
